export const levenshteinDistance = (a: string, b: string): number => {
    const matrix: number[][] = [];

    // Initialize the first row and column of the matrix
    for (let i = 0; i <= b.length; i++) {
        matrix[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
        matrix[0][j] = j;
    }

    // Fill in the rest of the matrix
    for (let i = 1; i <= b.length; i++) {
        for (let j = 1; j <= a.length; j++) {
            if (b[i - 1] === a[j - 1]) {
                matrix[i][j] = matrix[i - 1][j - 1];
            } else {
                matrix[i][j] = Math.min(
                    matrix[i - 1][j - 1] + 1, // substitution
                    matrix[i][j - 1] + 1,     // insertion
                    matrix[i - 1][j] + 1      // deletion
                );
            }
        }
    }

    return matrix[b.length][a.length];
};

// Helper function to sanitize the input by replacing or removing unwanted characters
export const sanitizeString = (input: string): string => {
    // Replace certain characters with spaces
    const replacedString = input.replace(/[():\]\[,\\*-]/g, ' ');

    // Remove certain characters entirely
    const cleanedString = replacedString.replace(/["'`]/g, '');

    // Replace multiple consecutive spaces with a single space
    return cleanedString.replace(/\s+/g, ' ').trim();
};

// Helper function to calculate overlap score more intelligently
export const calculateSmarterOverlapScore = (searchedName: string, productName: string): number => {
    const searchWords = searchedName.split(' ');
    const productWords = productName.split(' ');

    let score = 0;

    searchWords.forEach((word, index) => {
        if (productWords.includes(word)) {
            score += 1; // Exact word match
        } else {
            // Partial matches (e.g., 'apple' matches 'apple', 'apple juice' or 'apple pie')
            productWords.forEach(productWord => {
                if (productWord.includes(word)) {
                    score += 0.5; // Partial match
                }
            });
        }

        // Bonus points for consecutive word matches
        if (index > 0 && productWords.includes(searchWords[index - 1] + ' ' + word)) {
            score += 0.25; // Bonus for correct word order
        }
    });

    return score;
};

// Function to find the index and weighted match score of the closest match
export const findClosestMatchIndex = (searchedName: string, productNames: string[]): { index: number, matchScore: number } => {
    // Sanitize the searched name
    const sanitizedSearchName = sanitizeString(searchedName);
    const maxOverlapScore = sanitizedSearchName.split(' ').length;
    console.log(sanitizedSearchName)
    // Step 1: Find the top 3 strings with the most overlap
    const topMatches = productNames
        .map((name, index) => {
            const sanitizedProductName = sanitizeString(name);
            console.log(sanitizedProductName)
            return {
                name: sanitizedProductName,
                index,
                overlapScore: calculateSmarterOverlapScore(sanitizedSearchName, sanitizedProductName),
                overlapNormalized: 0 // Will be calculated later
            };
        })
        .map(match => {
            match.overlapNormalized = match.overlapScore / maxOverlapScore;
            return match;
        })
        .sort((a, b) => b.overlapScore - a.overlapScore)
        .slice(0, 3);

    if (topMatches.every(match => match.overlapScore === 0)) return { index: -1, matchScore: 0 };

    // Step 2: Apply Levenshtein distance on the top 3 matches
    let closestMatchIndex = -1;
    let bestWeightedScore = -Infinity;

    topMatches.forEach(match => {
        const distance = levenshteinDistance(sanitizedSearchName, match.name);
        const levenshteinNormalized = 1 - (distance / Math.max(sanitizedSearchName.length, match.name.length));

        // Combine both scores with weighting factors
        const weightedScore = (0.75 * match.overlapNormalized) + (0.25 * levenshteinNormalized); // Adjust weights as needed

        if (weightedScore > bestWeightedScore) {
            bestWeightedScore = weightedScore;
            closestMatchIndex = match.index;
        }
    });

    return {
        index: closestMatchIndex,
        matchScore: bestWeightedScore
    };
};

/** Collect entity ids from leaf rows that pass the current AG Grid filter. */
export const getFilteredLeafIds = (node: any, idField = 'c_id'): number[] => {
    if (!node) return [];
    if (!node.group) {
        if (node.displayed === false) return [];
        const id = node.data?.[idField];
        return id != null ? [Number(id)] : [];
    }
    return (node.childrenAfterFilter || []).flatMap((child: any) => getFilteredLeafIds(child, idField));
};
